import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getArticlesByCategory, allArticles } from '@/mocks/articles';
import { categories } from '@/mocks/home';
import type { AnimeArticle } from '@/mocks/articles';

const categoryTabs = [
  { slug: 'guoman', label: '国产动漫', icon: 'ri-flag-2-line' },
  { slug: 'riman', label: '日本动漫', icon: 'ri-global-line' },
  { slug: 'juchang', label: '剧场版', icon: 'ri-film-line' },
];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'guoman');
  const [articles, setArticles] = useState<AnimeArticle[]>([]);
  const [sortBy, setSortBy] = useState<'rating' | 'update'>('update');
  const [showSortMenu, setShowSortMenu] = useState(false);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  useEffect(() => {
    let list = getArticlesByCategory(activeSlug);
    if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else {
      list = [...list].sort((a, b) => b.currentEpisode - a.currentEpisode);
    }
    setArticles(list);
  }, [activeSlug, sortBy]);

  const activeTab = categoryTabs.find((t) => t.slug === activeSlug) || categoryTabs[0];
  const categoryMeta = categories.find((c) => c.slug === activeSlug);

  useEffect(() => {
    document.title = `${activeTab.label} | 51动漫 - 在线观看`;
    document.querySelector('meta[name="description"]')?.setAttribute('content', `51动漫${activeTab.label}分类，共收录${articles.length}部动漫作品。每日更新热门番剧、经典国漫，方便动漫迷快速找到观看路径。`);
    document.querySelector('meta[name="keywords"]')?.setAttribute('content', `51动漫,${activeTab.label},动漫分类,动漫在线观看,国漫在线,动漫资源`);
  }, [activeTab, articles.length]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [activeSlug]);

  useEffect(() => {
    const existingScript = document.querySelector('script[type="application/ld+json"]');
    if (existingScript) existingScript.remove();
    const jsonLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'http://heiliaochigua.cn/' },
            { '@type': 'ListItem', position: 2, name: activeTab.label, item: `http://heiliaochigua.cn/category/${activeSlug}` },
          ],
        },
        {
          '@type': 'CollectionPage',
          name: `${activeTab.label} | 51动漫`,
          description: `51动漫${activeTab.label}分类页面，收录多部优质动漫作品`,
          url: `http://heiliaochigua.cn/category/${activeSlug}`,
          isPartOf: { '@id': 'http://heiliaochigua.cn/#website' },
        },
      ],
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(jsonLd);
    document.head.appendChild(script);
  }, [activeSlug, activeTab]);

  const renderStars = (rating: number) => {
    const full = Math.floor(rating);
    const stars = [];
    for (let i = 0; i < full; i++) {
      stars.push(<i key={`f-${i}`} className="ri-star-fill text-secondary-500 text-xs"></i>);
    }
    const remaining = 5 - full;
    for (let i = 0; i < remaining; i++) {
      stars.push(<i key={`e-${i}`} className="ri-star-line text-foreground-300 text-xs"></i>);
    }
    return stars;
  };

  return (
    <main className="min-h-screen">
      <section className="w-full px-4 md:px-6 lg:px-12 pt-20 pb-8 bg-background-100">
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-600 transition-colors">首页</Link>
          <i className="ri-arrow-right-s-line text-xs"></i>
          <span className="text-foreground-800 font-medium">{activeTab.label}</span>
        </nav>

        <div className="flex flex-col lg:flex-row lg:items-end gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading text-foreground-950">
              {activeTab.label}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              {categoryMeta ? `${categoryMeta.description} — 共 ${categoryMeta.count} 部作品` : `精选${activeTab.label}作品，每日更新`}
            </p>
          </div>
          <div className="lg:ml-auto relative">
            <button
              onClick={() => setShowSortMenu(!showSortMenu)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-700 hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-sort-desc text-base"></i>
              {sortBy === 'update' ? '按更新排序' : '按评分排序'}
              <i className={`ri-arrow-down-s-line text-sm transition-transform ${showSortMenu ? 'rotate-180' : ''}`}></i>
            </button>
            {showSortMenu && (
              <div className="absolute right-0 top-full mt-1 bg-background-50 border border-background-200 rounded-lg shadow-sm z-20 w-36">
                <button
                  onClick={() => { setSortBy('update'); setShowSortMenu(false); }}
                  className={`block w-full text-left px-4 py-2.5 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap ${sortBy === 'update' ? 'text-primary-600 font-medium' : 'text-foreground-700'}`}
                >
                  <i className="ri-time-line mr-2"></i>按更新排序
                </button>
                <button
                  onClick={() => { setSortBy('rating'); setShowSortMenu(false); }}
                  className={`block w-full text-left px-4 py-2.5 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap ${sortBy === 'rating' ? 'text-primary-600 font-medium' : 'text-foreground-700'}`}
                >
                  <i className="ri-star-line mr-2"></i>按评分排序
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1.5 mb-10">
          {categoryTabs.map((tab) => (
            <Link
              key={tab.slug}
              to={`/category/${tab.slug}`}
              onClick={() => setActiveSlug(tab.slug)}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                activeSlug === tab.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'text-foreground-600 hover:text-foreground-950 hover:bg-background-200/60'
              }`}
            >
              <i className={`${tab.icon} text-sm`}></i>
              {tab.label}
            </Link>
          ))}
        </div>
      </section>

      <section className="w-full px-4 md:px-6 lg:px-12 py-10 md:py-14 bg-background-50">
        {activeSlug === 'riman' || activeSlug === 'juchang' ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
              <i className={`${activeTab.icon} text-3xl text-foreground-400`}></i>
            </div>
            <h2 className="text-xl font-heading text-foreground-950 mb-2">即将上线</h2>
            <p className="text-sm text-foreground-500">该分类内容正在筹备中，敬请期待！先来看看国产动漫吧~</p>
            <Link
              to="/category/guoman"
              className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              去看看国产动漫 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {articles.map((anime) => (
                <Link
                  key={anime.slug}
                  to="/category/all"
                  className="group rounded-lg overflow-hidden bg-background-100 hover:bg-background-100/70 transition-colors"
                >
                  <div className="relative w-full h-[200px] overflow-hidden">
                    <img
                      src={anime.image}
                      alt={anime.title}
                      title={`${anime.title} - 51动漫国产动漫`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-3 left-3">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                        anime.status === '连载中' ? 'bg-accent-100 text-accent-700' : 'bg-secondary-100 text-secondary-700'
                      }`}>
                        {anime.status}
                      </span>
                    </div>
                    <div className="absolute top-3 right-3 flex items-center gap-0.5 bg-background-950/70 backdrop-blur-sm px-2 py-0.5 rounded-full">
                      <span className="text-background-50 text-xs font-medium">{anime.rating}</span>
                      <i className="ri-star-fill text-secondary-500 text-xs"></i>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-3 py-2">
                      <p className="text-background-50 text-xs">
                        {anime.updateDay} {anime.updateTime} 更新
                      </p>
                    </div>
                  </div>
                  <div className="p-3 md:p-4">
                    <div className="flex items-center gap-1 mb-1.5">
                      {renderStars(anime.rating)}
                    </div>
                    <h3 className="text-base font-heading text-foreground-950 group-hover:text-primary-600 transition-colors">
                      {anime.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{anime.alias}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs text-foreground-500">{anime.studio}</span>
                      <span className="text-xs text-foreground-400">|</span>
                      <span className="text-xs text-foreground-500">更新至 {anime.currentEpisode} 集</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2.5">
                      {anime.tags.slice(0, 3).map((tag) => (
                        <span key={tag} className="px-2 py-0.5 rounded-full text-xs bg-secondary-100 text-secondary-700">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>

            {articles.length === 0 && (
              <div className="text-center py-16">
                <p className="text-foreground-500 text-sm">暂无该分类的动漫作品</p>
              </div>
            )}

            <div className="mt-10 text-center">
              <p className="text-xs text-foreground-400">
                共 {articles.length} 部作品 — 51动漫持续更新中
              </p>
            </div>
          </>
        )}
      </section>
    </main>
  );
}